/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * SSOButtons Component - Educational SSO provider login buttons
 *
 * Displays login buttons for configured SSO providers:
 * - Clever (school rostering)
 * - ClassLink (school rostering)
 * - Google Workspace for Education
 *
 * Only shows buttons for providers that are configured on the server.
 */

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import PropTypes from 'prop-types'
import { Loader2 } from 'lucide-react'
import ssoService from '../../services/ssoService'
import { createLogger } from '../../utils/logger'

const logger = createLogger('SSOButtons')

// Provider icons as SVG components
const CleverIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93s3.05-7.44 7-7.93v15.86zm2-15.86c1.03.13 2 .45 2.87.93H13v-.93zM13 7h5.24c.25.31.48.65.68 1H13V7zm0 3h6.74c.08.33.15.66.19 1H13v-1zm0 3h6.93c-.04.34-.11.67-.19 1H13v-1zm0 3h6.24c-.2.35-.43.69-.68 1H13v-1zm0 3h5.11c-.87.48-1.84.8-2.87.93H13v-.93z" />
  </svg>
)

const ClassLinkIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M12 6v12M6 12h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
)

// Provider configurations
const PROVIDER_CONFIG = {
  clever: {
    name: 'Clever',
    Icon: CleverIcon,
    bgColor: 'bg-blue-600',
    hoverBgColor: 'hover:bg-blue-700',
    textColor: 'text-white',
    description: 'Sign in with your school account'
  },
  classlink: {
    name: 'ClassLink',
    Icon: ClassLinkIcon,
    bgColor: 'bg-cyan-600',
    hoverBgColor: 'hover:bg-cyan-700',
    textColor: 'text-white',
    description: 'Sign in with your school account'
  },
  google_edu: {
    name: 'Google',
    Icon: GoogleIcon,
    bgColor: 'bg-white',
    hoverBgColor: 'hover:bg-gray-50',
    textColor: 'text-gray-700',
    border: true,
    description: 'Sign in with Google Workspace'
  }
}

/**
 * SSOButtons Component
 *
 * Fetches available SSO providers from the server and displays
 * login buttons for each configured provider.
 *
 * @param {Object} props
 * @param {boolean} props.loading - External loading state
 * @param {boolean} props.disabled - Disabled state
 * @param {Function} props.onProviderClick - Optional callback when a provider is clicked
 */
const SSOButtons = ({ loading = false, disabled = false, onProviderClick }) => {
  const [providers, setProviders] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [clickedProvider, setClickedProvider] = useState(null)

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const data = await ssoService.getAvailableProviders()
        setProviders(data.providers || [])
      } catch (error) {
        logger.error('Failed to fetch SSO providers:', error)
        setProviders([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchProviders()
  }, [])

  const handleProviderClick = (providerId) => {
    if (loading || disabled || clickedProvider) return

    setClickedProvider(providerId)

    // Call optional callback
    if (onProviderClick) {
      onProviderClick(providerId)
    }

    // Redirect to SSO login
    ssoService.initiateLogin(providerId)
  }

  // Don't render anything if loading or no providers
  if (isLoading) {
    return null
  }

  if (providers.length === 0) {
    return null
  }

  return (
    <div className="space-y-3">
      {/* School SSO Section */}
      <div className="text-center mb-4">
        <span className="text-sm text-gray-500 dark:text-gray-400">Sign in with your school</span>
      </div>

      {/* Provider Buttons */}
      {providers.map((provider, index) => {
        const config = PROVIDER_CONFIG[provider.id] || {
          name: provider.name || provider.id,
          Icon: () => <span className="w-5 h-5">🔑</span>,
          bgColor: 'bg-gray-600',
          hoverBgColor: 'hover:bg-gray-700',
          textColor: 'text-white'
        }

        const { Icon } = config
        const isProviderLoading = clickedProvider === provider.id

        return (
          <motion.button
            key={provider.id}
            type="button"
            onClick={() => handleProviderClick(provider.id)}
            disabled={loading || disabled || !!clickedProvider}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * index }}
            whileHover={{ scale: disabled || loading || clickedProvider ? 1 : 1.01 }}
            whileTap={{ scale: disabled || loading || clickedProvider ? 1 : 0.99 }}
            className={`
              w-full flex items-center justify-center space-x-3
              px-4 py-2.5 rounded-lg
              transition-all duration-200
              text-sm font-medium
              ${config.bgColor}
              ${config.hoverBgColor}
              ${config.textColor}
              ${config.border ? 'border border-gray-300 dark:border-gray-600' : ''}
              disabled:opacity-50 disabled:cursor-not-allowed
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
            `}
            aria-label={`Sign in with ${config.name}`}
          >
            {isProviderLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Redirecting to {config.name}...</span>
              </>
            ) : (
              <>
                <Icon />
                <span>Continue with {config.name}</span>
              </>
            )}
          </motion.button>
        )
      })}

      {/* Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">
            or sign in with email
          </span>
        </div>
      </div>
    </div>
  )
}

SSOButtons.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onProviderClick: PropTypes.func
}

export default SSOButtons
